/**
 * Camlin Companion — site-key + control-plane config contracts (doc 116 §2.6).
 * CO0 scaffolding only; server-side enforcement lands in CO1+.
 */

/** Publishable site key: identifies the site, never authorizes (doc 113 posture). */
export type CompanionSiteKey = `cmp_${string}_${string}`;

export type CompanionRiskLevel = 'safe' | 'mutating' | 'destructive' | 'spend';

export interface CompanionKnowledgeSource {
  kind: 'sitemap' | 'url' | 'document' | 'fact';
  /** sitemap URL, page URL, document id, or free-text fact body */
  value: string;
  label?: string;
}

export interface CompanionPersonaConfig {
  displayName: string;
  specialistPrompt: string;
}

export interface CompanionAvatarThemeConfig {
  modelId?: string;
  voiceId?: string;
  position?: 'bottom-right' | 'bottom-left';
  theme?: 'light' | 'dark' | 'auto';
  /**
   * Short call-to-action shown as a pill beside the collapsed launcher
   * badge (e.g. "Chat with our avatar"). A bare unlabeled circle tested
   * as unrecognisable on the avatar.* pilot (Keith, 5 Aug 2026); when
   * unset the widget falls back to "Chat with <persona.displayName>".
   */
  launcherLabel?: string;
}

export interface CompanionProactiveTrigger {
  kind: 'page_dwell' | 'scroll_depth' | 'exit_intent' | 'page_pair' | 'campaign_url';
  /** Page path pattern, scroll fraction 0–1, campaign query match, etc. */
  match: string;
  /** Minimum dwell seconds for page_dwell triggers */
  dwellSeconds?: number;
  /** Scroll depth threshold 0–1 for scroll_depth triggers */
  scrollFraction?: number;
  /** Short bubble copy shown before the visitor clicks to engage */
  nudgeMessage?: string;
}

/** D3 conservative caps — shipped ON by default (doc 116 §2.4, §7 D3). */
export interface CompanionProactiveCaps {
  enabled: boolean;
  maxNudgesPerPage: number;
  maxNudgesPerSession: number;
  dismissCoolOffDays: number;
}

export interface CompanionProactiveRules {
  triggers: CompanionProactiveTrigger[];
  caps: CompanionProactiveCaps;
}

export interface CompanionActionRiskOverride {
  actionId: string;
  risk: CompanionRiskLevel;
}

export interface CompanionTranscriptRetention {
  /** Retention window in days; short default per doc 116 §2.4 */
  retentionDays: number;
  enabled: boolean;
}

/**
 * Structural representation of the live page (Companion v6, 6 Aug 2026)
 * — the widget-side twin of the rendered crawler's capture: what an
 * answer (and later an operate-mode agent) needs to reason about the
 * page the visitor is on RIGHT NOW, including dynamic pages the crawl
 * never saw. Mirrored byte-for-byte server-side as
 * `CompanionPageStructure` in
 * `apps/schema-service/src/companion/types.ts` — keep both in sync.
 */
export interface CompanionPageStructure {
  /** h1–h6 in document order. */
  headings: Array<{ level: number; text: string }>;
  /** ARIA/HTML5 landmark regions with their accessible labels when present. */
  landmarks: Array<{ role: string; label?: string }>;
  /** Visible in-page links (text + resolved href), deduplicated. */
  links: Array<{ text: string; href: string }>;
  /** Visible buttons / button-role elements by their accessible text. */
  buttons: Array<{ text: string }>;
  /** Forms with their labelled fields — the CO4 form-fill substrate. */
  forms: Array<{
    name?: string;
    action?: string;
    fields: Array<{ label?: string; name?: string; type: string; required?: boolean; placeholder?: string }>;
    submitLabel?: string;
  }>;
  metaDescription?: string;
  capturedAt: string;
  /** How this structure was captured — 'companion-live-bridge' for the widget. */
  renderer: string;
}

/**
 * Live page context extracted by the CO2 bridge (doc 116 §2.1.2) — the
 * "current URL, title, headings, and a bounded visible-text digest" the
 * Companion re-sends with each turn and on SPA navigation. Mirrored
 * byte-for-byte server-side as `CompanionPageContext` in
 * `apps/schema-service/src/companion/types.ts` — keep both in sync.
 */
export interface CompanionPageContext {
  url: string;
  title: string;
  headings: string[];
  visibleTextDigest: string;
  capturedAt: string;
  /** Structured page digest (Companion v6) — optional; absent from older bundles. */
  structure?: CompanionPageStructure;
}

export interface CompanionManifestActionEntry {
  id: string;
  label: string;
  description: string;
  sectionId?: string;
  risk: CompanionRiskLevel;
  paramsSchema?: Record<string, unknown>;
}

export interface CompanionManifestFieldEntry {
  id: string;
  label: string;
  description?: string;
  inputType?: 'text' | 'email' | 'tel' | 'textarea' | 'select';
}

export interface CompanionManifestSectionEntry {
  id: string;
  label: string;
  description?: string;
}

/** Annotation manifest pack hosted in site config (doc 116 §2.2). */
export interface CompanionAnnotationManifestPack {
  version: number;
  sections: CompanionManifestSectionEntry[];
  actions: CompanionManifestActionEntry[];
  fields: CompanionManifestFieldEntry[];
}

/**
 * Which speech-to-text engine the widget's mic path uses (6 Aug 2026).
 * Default (absent, or engine 'azure') keeps the historical Azure path:
 * browser Azure Speech SDK with a token from `/talk/get-speech-token`.
 * With engine 'camlin', recognition goes through the Camlin Speech STT
 * API — `tokenUrl` names a server route on the embedding site that holds
 * the real API key and mints short-lived `cat_…` tokens via
 * `POST /api/v1/asr/token`; the widget then POSTs WAV turns to
 * `recognizeUrl` (default `/api/v1/asr/recognize`). The token's tenant
 * binds lexicon biasing, so word injection applies to the mic.
 */
export interface CompanionAsrConfig {
  engine: 'azure' | 'camlin';
  tokenUrl?: string;
  recognizeUrl?: string;
  language?: string;
}

export interface CompanionSiteConfig {
  siteKey: CompanionSiteKey;
  tenantId: string;
  siteName: string;
  knowledgeSources: CompanionKnowledgeSource[];
  persona: CompanionPersonaConfig;
  avatar: CompanionAvatarThemeConfig;
  allowedOrigins: string[];
  allowedActionIds: string[];
  actionRiskOverrides: CompanionActionRiskOverride[];
  proactive: CompanionProactiveRules;
  transcriptRetention: CompanionTranscriptRetention;
  manifestPack: CompanionAnnotationManifestPack;
  /**
   * Explicit runtime API base for the live contextual-chat session
   * (threaded into `AvatarEmbedConfig.runtimeUrl`). Without it the SDK
   * resolves the bare platform origin (`useAvatarConfig`'s
   * `resolveCamlinPlatformOrigin`), which stopped proxying
   * `/session-stream` when the apex became the promo front door
   * (5 Aug 2026 cutover). Pilot configs set this to
   * `https://architect.camlinconnect.com.au/runtime-api` — the builder
   * host's documented runtime proxy (WS upgrade capable); the visual
   * channel URL strips the `/runtime-api` suffix itself (liveMode.ts).
   */
  runtimeUrl?: string;
  /**
   * REAL platform tenant id (`tenant_<ts>_<rand>`) for the runtime's
   * entitlement gate, when the site should bill against a specific
   * tenant. Distinct from `tenantId` on purpose: that field is the
   * control-plane slug ('camlin', 'nuamedia') and does NOT exist in the
   * runtime tenant manager — sending it as the session tenant fails
   * closed with entitlement-denied/tenant-not-found (observed live,
   * 5 Aug 2026, avatar.* pilot). When unset the session omits tenantId
   * and the runtime applies its SESSION_FALLBACK_TENANT_ID — the
   * documented path for channels that cannot carry a tenant on the wire.
   */
  runtimeTenantId?: string;
  /**
   * Explicit voice-api base for WebRTC/SIP provisioning (threaded into
   * `AvatarEmbedConfig.voiceUrl`). Same disease as `runtimeUrl`: the SDK
   * default resolves `<platform-origin>/voice-api`, which the apex
   * stopped proxying at the 5 Aug 2026 promo cutover — every Companion
   * mount then burned a cross-origin provision attempt against a host
   * that CORS-rejects it. The avatar.* pilot sets this to its own
   * origin's `/voice-api` (same origin, no CORS, live camlin-voice).
   */
  voiceUrl?: string;
  /** Mic STT engine selection; absent = Azure (historical default). */
  asr?: CompanionAsrConfig;
  /**
   * Base URL for browser-reachable Companion control-plane routes
   * (`/generate/companion-greeting`, v6). Unset = same-origin relative —
   * correct wherever the site's edge routes `/generate/*` to
   * schema-service (the speech site's shared ALB does). Calls fail soft
   * on properties whose edge doesn't.
   */
  companionApiUrl?: string;
}

export type CompanionBootRefusalReason =
  | 'invalid_site_key'
  | 'origin_refused'
  | 'config_unavailable'
  | 'site_consent_gate';

export interface CompanionBootResult {
  ok: boolean;
  config?: CompanionSiteConfig;
  refusalReason?: CompanionBootRefusalReason;
  message?: string;
}

export interface CompanionControlPlaneFetchOptions {
  siteKey: CompanionSiteKey;
  /** Sent as Origin header; origin allow-list is enforced server-side in CO1. */
  origin: string;
  controlPlaneBaseUrl?: string;
}

export interface CompanionControlPlaneClient {
  fetchSiteConfig(options: CompanionControlPlaneFetchOptions): Promise<CompanionBootResult>;
}

/** Default proactive caps per D3 (doc 116 §7). */
export const DEFAULT_COMPANION_PROACTIVE_CAPS: CompanionProactiveCaps = {
  enabled: true,
  maxNudgesPerPage: 1,
  maxNudgesPerSession: 2,
  dismissCoolOffDays: 7
};

export const DEFAULT_COMPANION_TRANSCRIPT_RETENTION: CompanionTranscriptRetention = {
  enabled: true,
  retentionDays: 30
};
